import { AuthenticationLayout } from 'components/AuthenticationLayout';
import MaintenancePage from 'components/MaintenancePage';
import ProtectedRoute from 'components/ProtectedRoute';
import RoleBasedRedirect from 'components/RoleBasedRedirect';
import AdminDashboard from 'pages/Admin/AdminDashboard';
import AdminOrganizationPage from 'pages/Admin/AdminOrganizationPage';
import AdminProjects from 'pages/Admin/AdminProjects';
import AdminRevisionsPage from 'pages/Admin/AdminRevisionsPage';
import AdminTransactionsPage from 'pages/Admin/AdminTransactionsPage';
import AdminOrganizationDetails from 'pages/Admin/OrganizationDetails';
import InviteSignUp from 'pages/InviteSignUp';
import LoginGate from 'pages/LoginGate';
import SignUpByInvite from 'pages/SignUpByInvite';
import { lazy } from 'react';
import { Navigate, RouteObject } from 'react-router-dom';
import { USER_ROLES } from 'utils/constants';
import { Layout } from './components/Layout';
import AdminUserPage from './pages/Admin/AdminUserPage';
import AdminLoginPage from './pages/AdminLogin';
import { default as BusinessAccountPage, default as EditBusinessPage } from './pages/BusinessAccount/EditBusinessAccount';
import ContactUsPage from './pages/ContactUs';
import ForgotPasswordPage from './pages/ForgotPassword';
import HowItWorksPage from './pages/HowItWorks';
import PublicAnalyzePage from './pages/PublicAnalyze/PublicAnalyzePage';
import PublicAnalyzeResumePage from './pages/PublicAnalyze/PublicAnalyzeResumePage';
import ZoneNamesPage from './pages/ZoneNames/ZoneNamesPage';
import Iframe3DViewer from './pages/Iframe3DViewer';
import IframeShgcViewer from './pages/IframeShgcViewer';
import IframeCvViewer from './pages/IframeCvViewer';
import IframeIDFViewer from './pages/IframeIDFViewer';
import EnergyPlusViewer from './pages/EnergyPlusViewer';
import AdminInspectionsListPage from './pages/Admin/AdminInspectionsListPage';
import InspectorInspectionsPage from './pages/Inspector/InspectorInspectionsPage';
import MyChecksPage from './pages/MyChecks/MyChecksPage';
import LoginPage from './pages/Login';
import PricingPage from './pages/Pricing2';
import ProjectsDashBoard from './pages/Projects/ProjectPage';
import RevisionsPage from './pages/Revisions/RevisionPage';
import SignUp from "./pages/SignUp";
import EditUserPage from './pages/UserEdit/EditUser';
import RevisionResultGate from './pages/RevisionResultGate';
export const routePaths = {
  home: '',
  login: 'login',
}

// Composants avec lazy loading pour les fonctionnalités moins critiques
const NewProjectPage = lazy(() => import('./pages/Projects/NewProject'))
const EditProjectPage = lazy(() => import('./pages/Projects/EditProject'))
const NoMatchPage = lazy(() => import('./pages/NoMatch'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPassword'));
const SignupPage = SignUp ;
const AddRevisionPage =lazy(() => import('./pages/Revisions/NewRevision'))
const AddRevisionLabsPage = lazy(() => import('./pages/Revisions/IdfLab'))
// Hidden until CAD to DXF is ready to ship
// const Cad2DxfPage = lazy(() => import('./pages/Cad2Dxf/Cad2DxfPage'))
const RevisionDiagnosisPage = lazy(() => import('./pages/Revisions/RevisionDiagnosisPage'))
const EditRevisionPage =lazy(() => import('./pages/Revisions/EditRevision'));
const EditRevisionTicketDSBPage = lazy(() => import('./pages/Revisions/EditTicketDsb'))
const ReportJsonViewer = lazy(() => import('./pages/ReportJsonViewer'))
const InspectionGate = lazy(() => import('./pages/InspectionGate'))
const AdminTicketsPage = lazy(() => import('./pages/Admin/AdminTickets'));
const AdminOpenTicketPage = lazy(() => import('./pages/Admin/ticket_resolution/AdminOpenTicketPage'))

export const getRoutes = (role?: string): RouteObject[] => {
  return role && role.toLowerCase() === USER_ROLES.ADMIN
    ? [...AdminUserRoutes, ...publicRoutes]
    : [...AuthUserRoutes, ...publicRoutes];
};

export const publicRoutes: RouteObject[] = [
  {
    path: '/',
    children: [
      {
        path: '/maintenance',
        element: <MaintenancePage />,
      },
      {
        path: '/login',
        element: <AuthenticationLayout page='login' />,
        children: [
          { index: true, element: <LoginPage /> },
          { path: '*', element: <LoginPage /> },
        ],
      },
      {
        path: '/login/gate/:loginDetails',
        element: <AuthenticationLayout page='login' />,
        children: [
          { index: true, element: <LoginGate /> },
          { path: '*', element: <LoginGate /> },
        ],
      },
      {
        path: '/forgot',
        element: <AuthenticationLayout page='forgot' />,
        children: [
          { index: true, element: <ForgotPasswordPage /> },
          { path: '*', element: <ForgotPasswordPage /> },
        ],
      },
      {
        path: '/reset/forgot/:token',
        element: <AuthenticationLayout page='reset' />,
        children: [
          { index: true, element: <ResetPasswordPage /> },
          { path: '*', element: <ResetPasswordPage /> },
        ],
      },
      {
        path: '/admin/login',
        element: <AuthenticationLayout page='adminLogin' />,
        children: [
          { index: true, element: <AdminLoginPage /> },
          { path: '*', element: <AdminLoginPage /> },
        ],
      },
      {
        path: '/signup',
        element: <Layout />,
        children: [
          { index: true, element: <SignupPage /> },
          { path: '*', element: <SignupPage /> },
        ],
      },
      {
        path: '/contact',
        element: <Layout />,
        children: [
          { index: true, element: <ContactUsPage /> },
          { path: '*', element: <ContactUsPage /> },
        ],
      },
      {
        path: '/how-it-works',
        element: <Layout />,
        children: [
          { index: true, element: <HowItWorksPage /> },
          { path: '*', element: <HowItWorksPage /> },
        ],
      },
      {
        path: '/try',
        element: <Layout />,
        children: [
          { index: true, element: <PublicAnalyzePage /> },
        ],
      },
      {
        path: '/fix',
        element: <Layout />,
        children: [
          { index: true, element: <ZoneNamesPage /> },
        ],
      },
      {
        path: '/try/resume',
        element: <Layout />,
        children: [
          {
            index: true,
            element: (
              <ProtectedRoute>
                <PublicAnalyzeResumePage />
              </ProtectedRoute>
            ),
          },
        ],
      },
      {
        path: '/pricing',
        element: <Layout />,
        children: [
          { index: true, element: <ProtectedRoute>
            <PricingPage />
          </ProtectedRoute> },
        ],
      },
      {
        path: '/invite/gate/:invitationDetails',
        element: <Layout />,
        children: [
          { index: true, element: <InviteSignUp /> },
          { path: '*', element: <InviteSignUp /> },
        ],
      },
      {
        path: '/signupbyinvite',
        element: <Layout />,
        children: [
          { index: true, element: <SignUpByInvite /> },
          { path: '*', element: <SignUpByInvite /> },
        ],
      },
      {
        path: '/3d-viewer',
        element: <Iframe3DViewer />,
      },
      {
        path: '/shgc-viewer',
        element: <IframeShgcViewer />,
      },
      {
        path: '/cv-viewer',
        element: <IframeCvViewer />,
      },
      {
        path: '/idf-viewer',
        element: <IframeIDFViewer />,
      },
      {
        path: '/report/:revisionId/check',
        element: (
          <ProtectedRoute>
            <InspectionGate />
          </ProtectedRoute>
        ),
      },
      {
        path: '/report/:revisionId',
        element: (
          <ProtectedRoute>
            <ReportJsonViewer />
          </ProtectedRoute>
        ),
      },
    ],
  },
];

export const AuthUserRoutes: RouteObject[] = [
  {
    path: '/',
    element: <Layout />,
    children: [
     
      {
        index: true,
        element: (
          <ProtectedRoute>
            <RoleBasedRedirect />
          </ProtectedRoute>
        ),
      },
      {
        path: '/checks',
        element: (
          <ProtectedRoute>
            <MyChecksPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/inspections',
        element: (
          <ProtectedRoute>
            <InspectorInspectionsPage />
          </ProtectedRoute>
        ),
      },
      // {
      //   path: '/cad-to-dxf',
      //   element: (
      //     <ProtectedRoute>
      //       <Cad2DxfPage />
      //     </ProtectedRoute>
      //   ),
      // },
      {
        path: '/dashboard',
        element: (
          <ProtectedRoute>
            <ProjectsDashBoard />
          </ProtectedRoute>
        ),
      },
      {
        path: '/add-project',
        element: (
          <ProtectedRoute>
            <NewProjectPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/edit-project/:projectId',
        element: (
          <ProtectedRoute>
            <EditProjectPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/edit/business',
        element: (
          <ProtectedRoute>
            <EditBusinessPage mode={"edit"} />
          </ProtectedRoute>
        ),
      },
      {
        path: '/edit/user',
        element: (
          <ProtectedRoute>
            <EditUserPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/project/:projectId/board',
        element: (
          <ProtectedRoute>
            <RevisionsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/project/:projectId/add-revision',
        element: (
          <ProtectedRoute>
            <AddRevisionPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/project/:projectId/add-revision-labs',
        element: (
          <ProtectedRoute>
            <AddRevisionLabsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/project/:projectId/edit-revision-labs/:revisionId',
        element: (
          <ProtectedRoute>
            <AddRevisionLabsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/project/:projectId/edit-revision/:revisionId',
        element: (
          <ProtectedRoute>
            <EditRevisionPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/project/:projectId/revision/:revisionId/diagnosis',
        element: (
          <ProtectedRoute>
            <RevisionDiagnosisPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/business/:businessId/project/:projectId/revision/:revisionId/diagnosis',
        element: (
          <ProtectedRoute>
            <RevisionDiagnosisPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/business/:businessId/project/:projectId/edit-revision/:revisionId',
        element: (
          <ProtectedRoute>
            <EditRevisionPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/business/:businessId/project/:projectId/revision/:revisionId/result',
        element: (
          <ProtectedRoute>
            <RevisionResultGate />
          </ProtectedRoute>
        ),
      },
      {
        path: '/project/:projectId/edit-ticket-dsb/:revisionId',
        element: (
          <ProtectedRoute>
            <EditRevisionTicketDSBPage />
          </ProtectedRoute>
        ),
      },
      { path: '*', element: <NoMatchPage /> },
    ],
  },
];

export const AdminUserRoutes: RouteObject[] = [
  {
    path: '/',
    element: <Layout />,
    children: [
      {
        path: '/',
        element: (
          <ProtectedRoute>
            <Navigate to="/admin/dashboard" replace />
          </ProtectedRoute>
        ),
      },
      {
        path: 'admin/dashboard',
        element: (
          <ProtectedRoute>
            <AdminDashboard />
          </ProtectedRoute>
        ),
      },
      {
        path: 'admin/tickets',
        element: (
          <ProtectedRoute>
            <AdminTicketsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'admin/revisions',
        element: (
          <ProtectedRoute>
            <AdminRevisionsPage />
          </ProtectedRoute>
        ),
      },
      // {
      //   path: 'admin/cad-to-dxf',
      //   element: (
      //     <ProtectedRoute>
      //       <Cad2DxfPage />
      //     </ProtectedRoute>
      //   ),
      // },
      {
        path: 'admin/stats',
        element: (
          <ProtectedRoute>
            <AdminOrganizationPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'admin/users',
        element: (
          <ProtectedRoute>
            <AdminUserPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'admin/organization/:organizationId',
        element: (
          <ProtectedRoute>
            <AdminOrganizationDetails />
          </ProtectedRoute>
        ),
      },
      {
        path: 'admin/transactions',
        element: (
          <ProtectedRoute>
            <AdminTransactionsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'admin/projects',
        element: (
          <ProtectedRoute>
            <AdminProjects />
          </ProtectedRoute>
        ),
      },
      {
        path: 'admin/inspections',
        element: (
          <ProtectedRoute>
            <AdminInspectionsListPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'admin/inspections/:organizationId',
        element: (
          <ProtectedRoute>
            <InspectorInspectionsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'admin/business/:businessId/projects',
        element: (
          <ProtectedRoute>
            {/* <AdminBusinessProjects /> */}
            <ProjectsDashBoard />
          </ProtectedRoute>
        ),
      },
      {
        path: '/edit-project/:projectId/:businessId',
        element: (
          <ProtectedRoute>
            <EditProjectPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/edit/business/:businessId',
        element: (
          <ProtectedRoute>
            <BusinessAccountPage mode={"edit"} />
          </ProtectedRoute>
        ),
      },
      {
        path: '/admin/add-business',
        element: (
          <ProtectedRoute>
            <BusinessAccountPage mode={"create"} />
          </ProtectedRoute>
        ),
      },
      {
        path: '/add-project/:businessId',
        element: (
          <ProtectedRoute>
            <NewProjectPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'admin/business/:businessId/project/:projectId/board',
        element: (
          <ProtectedRoute>
            <RevisionsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'admin/business/:businessId/project/:projectId/revision/:revisionId/result',
        element: (
          <ProtectedRoute>
            <RevisionResultGate />
          </ProtectedRoute>
        ),
      },
      {
        path: 'admin/business/:businessId/project/:projectId/revision/:revisionId/diagnosis',
        element: (
          <ProtectedRoute>
            <RevisionDiagnosisPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/admin/business/:businessId/project/:projectId/add-revision',
        element: (
          <ProtectedRoute>
            <AddRevisionPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/admin/business/:businessId/project/:projectId/add-revision-labs',
        element: (
          <ProtectedRoute>
            <AddRevisionLabsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/admin/business/:businessId/project/:projectId/edit-revision-labs/:revisionId',
        element: (
          <ProtectedRoute>
            <AddRevisionLabsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/admin/business/:businessId/project/:projectId/edit-revision/:revisionId',
        element: (
          <ProtectedRoute>
            <EditRevisionPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/admin/business/:businessId/project/:projectId/edit-ticket-dsb/:revisionId',
        element: (
          <ProtectedRoute>
            <EditRevisionTicketDSBPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/admin/business/:businessId/project/:projectId/revision/:revisionId/:ticketId',
        element: (
          <ProtectedRoute>
            <AdminOpenTicketPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/energy-viewer',
        element: <EnergyPlusViewer />,
      },
      { path: '*', element: <NoMatchPage /> },
    ],
  },
];